import * as jobService from '../../services/JobService';
import { normalizeJson } from '../../../shared/helpers';
import { defineStore } from "pinia";
import { useRepo } from "pinia-orm";
import Job from '../../models/Job';

function initialState() {
    return {
        addingJob: false,
        editingJob: null,
        archivingJob: null,
        unarchivingJob: null,
        deletingJob: null,
        fetchingJobs: false,
        allJobsFetched: {
            active: false,
            archived: false,
        },
        selectedApplications: [],
        stagesWithAllApplicationsChecked: [],
        draggingApplications: [],
        fetchingAvailableParameters: false,
        availableParameters: {},
        vdabEditorDrafts: {},
        paginatedResult: {},
        applicationsFilter: {},
        fetchingSharedInterimEmails: false,
        sharedInterimEmails: [],
    };
}

function _fetchInterimJobEmails(state, jobId) {
    state.fetchingSharedInterimEmails = true;
    jobService.sharedInterimEmails(jobId).then((response) => {
        state.sharedInterimEmails = response;
        state.fetchingSharedInterimEmails = false;
    });
}

export const useJobsStore = defineStore('jobsModule', {
    state: () => ({
        ...initialState(),
        activeState: 'active',
        search: '',
        locationFilter: null,
        userFilter: null,
        tagsFilter: [],
        createDateStart: null,
        createDateEnd: null,
        publishDateStart: null,
        publishDateEnd: null,
        archiveDateStart: null,
        archiveDateEnd: null,
        region: null,
        jobReasonAccepted: null,
        jobReasonRejected: null,
        jobRepo: useRepo(Job),

    }),
    actions: {
        fetch({ filters, activeState, page, all = 0 }) {
            this.START_FETCHING_JOBS(activeState);

            if (!all) {
                this.jobRepo.flush();
            }

            return jobService.index({
                filters: {
                    ...(activeState ? { active: { active: 1, archived: 0 }[activeState] } : {}),
                    ...filters,
                },
                page,
                all,
            }).then(result => {
                this.setPaginateResults(result);

                if (all) {
                    return this.jobRepo.save(normalizeJson(result.data));
                }

                return this.jobRepo.save(normalizeJson(result.data.data));
            }).then((inserts) => {
                this.STOP_FETCHING_JOBS();

                if (typeof inserts !== 'undefined' && inserts.length !== 0) {
                    this.setAllJobsFetched(activeState);
                }
            });
        },
        resort(sortedJobs) {

            const sort = sortedJobs.map((job, index) => ({ id: job.id, sequence: index }));

            const original = this.jobRepo.where('id', sort.map(sort => sort.id)).get().map(job => ({
                id: job.id,
                sequence: job.sequence,
            }));

            this.jobRepo.save(sort);

            return jobService.updateSort(sort)
                .catch(() => {
                    this.jobRepo.save(original);
                });
        },
        clear() {
            this.jobRepo.flush();
            this.resetState();
        },
        updateSelectedApplications({ applications }) {
            this.setSelectedApplications(applications);
        },
        resetState() {
            Object.assign(this, initialState());
        },
        SET_ACTIVE_STATE(activeState) {
            this.activeState = activeState;
        },
        SET_SEARCH_INPUT(search) {
            this.search = search;
        },
        SET_CREATE_DATE_START_FILTER(value) {
            this.createDateStart = value;
        },
        SET_CREATE_DATE_END_FILTER(value) {
            this.createDateEnd = value;
        },
        SET_PUBLISH_DATE_START_FILTER(value) {
            this.publishDateStart = value;
        },
        SET_PUBLISH_DATE_END_FILTER(value) {
            this.publishDateEnd = value;
        },
        SET_ARCHIVE_DATE_START_FILTER(value) {
            this.archiveDateStart = value;
        },
        SET_ARCHIVE_DATE_END_FILTER(value) {
            this.archiveDateEnd = value;
        },
        SET_JOB_REASON_ACCEPTED_FILTER(value) {
            this.jobReasonAccepted = value;
        },
        SET_JOB_REASON_REJECTED_FILTER(value) {
            this.jobReasonRejected = value;
        },
        SET_REGION_FILTER(value) {
            this.region = value;
        },
        SET_LOCATION_FILTER(location) {
            this.locationFilter = location;
        },
        SET_USER_FILTER(user) {
            this.userFilter = user;
        },
        SET_TAGS_FILTER(tags) {
            this.tagsFilter = tags;
        },
        START_FETCHING_JOBS(activeState) {
            this.fetchingJobs = activeState;
        },
        STOP_FETCHING_JOBS() {
            this.fetchingJobs = false;
        },
        SET_FETCHING_AVAILABLE_PARAMETERS(activeState) {
            this.fetchingAvailableParameters = activeState;
        },
        setAllJobsFetched(activeState) {
            this.allJobsFetched[activeState] = true;
        },
        startAddingJob() {
            this.addingJob = true;
        },
        startEditingJob(job) {
            this.editingJob = job;
        },
        cancelAddingJob() {
            this.addingJob = false;
        },
        cancelEditingJob() {
            this.editingJob = null;
        },
        startArchivingJob(job) {
            this.archivingJob = job;
            _fetchInterimJobEmails(this, job.id);
        },
        cancelArchivingJob() {
            this.archivingJob = null;
        },
        confirmDelete(job) {
            this.deletingJob = job;
        },
        cancelDeletingJob() {
            this.deletingJob = null;
        },
        cancelUnarchivingJob() {
            this.unarchivingJob = null;
        },
        startUnarchivingJob(job) {
            this.unarchivingJob = job;
        },
        setSelectedApplications(selection) {
            this.selectedApplications = selection;
        },
        clearSelectedApplications() {
            this.selectedApplications = [];
            this.stagesWithAllApplicationsChecked = [];
        },
        setStagesWithAllApplicationsChecked(values) {
            this.stagesWithAllApplicationsChecked = values;
        },
        startDraggingApplications(applications = []) {
            this.draggingApplications = applications;
        },
        stopDraggingApplications() {
            this.draggingApplications = [];
        },
        setAvailableParameters({ contract_type, status, timing, type, wanted_experience, locations, place_of_work }) {
            this.availableParameters = { contract_type, status, timing, type, wanted_experience, locations, place_of_work };
        },
        saveVdabDraft({ job, form }) {
            this.vdabEditorDrafts[job] = form;
        },
        setPaginateResults(jobs) {
            this.paginatedResult = jobs;
        },
        setApplicationsFilter({ id, key, value }) {
            const filters = this.applicationsFilter[id] || {};
            filters[key] = value;
            this.applicationsFilter[id] = filters;
        }
    },
});
